import { isUndefined, min } from 'lodash/fp';
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import { ARROW_WIDTH_OFFSET, Carousel } from '../../../shared/carousel/Carousel';
import { Engine, Globe, MultiAnalytics, Table } from '../../../shared/icons';
import { HomeButtonTile } from '../HomeButton';
import styles from '../Modules.module.scss';
import { MinifiedOpinionAverageChart } from './MinifiedOpinionAverageChart';
import { OpinionsMap } from './OpinionsMap';
import { useSystemColourPreference } from '../../../../hooks/useSystemColourPreference';
import { HomePageLink, SystemMaintenance } from '../../../admin/system-maintenance/store';

const CAROUSEL_TIMEOUT = 15000; // 15 seconds
const TITLE_HEIGHT = 51;
const MODULE_PADDING = 20;

interface OpinionsModuleProps {
    dimensions?: {
        height: number;
        width: number;
    }
    reduceMapHeight: boolean;
    analyticsSpinner: boolean;
    shouldRightAlignTitle: boolean;
    tileDisabledTooltip: (tile: HomePageLink) => string | undefined;
    defaultOpinionView: string;
    systemMaintenanceIds: number[];
}

export const OpinionsModule: React.FC<OpinionsModuleProps> = ({ dimensions, reduceMapHeight, analyticsSpinner, shouldRightAlignTitle, tileDisabledTooltip, defaultOpinionView, systemMaintenanceIds }) => {
    const showDarkTheme = useSystemColourPreference();

    const chartDimensions = useMemo(() => {
        if (isUndefined(dimensions)) {
            return { height: 0, width: 0 };
        }
        return { height: dimensions.height - TITLE_HEIGHT, width: (dimensions.width / 2) - (ARROW_WIDTH_OFFSET + MODULE_PADDING) };
    }, [dimensions]);

    const linksDisabled = useMemo(() => [SystemMaintenance.OPINIONS, SystemMaintenance.OPINION_ANALYTICS].some(systemMaintenanceId => systemMaintenanceIds.includes(systemMaintenanceId)), [systemMaintenanceIds]);

    const rotatingCharts = useMemo(() => linksDisabled ?
        [
            <OpinionsMap key='opinions-map' dimensions={chartDimensions} analyticsSpinner={analyticsSpinner} reduceMapHeight={reduceMapHeight} />,
            <MinifiedOpinionAverageChart key='opinion-average' dimensions={chartDimensions} />
        ] : [
            <Link key='opinions-map' to='/opinions/analytics/jurisdiction-coverage' className={styles.linkWrapper}><OpinionsMap dimensions={chartDimensions} analyticsSpinner={analyticsSpinner} reduceMapHeight={reduceMapHeight} /></Link>,
            <Link key='opinion-average' to='/opinions/analytics/opinions-average' className={styles.linkWrapper}><MinifiedOpinionAverageChart dimensions={chartDimensions} /></Link>
        ]
    , [linksDisabled, chartDimensions, analyticsSpinner, reduceMapHeight]);

    const iconSize = useMemo(() => min([chartDimensions.height / 4, chartDimensions.width / 2]), [chartDimensions]);

    const chartDisplay = useMemo(() => (
        <Carousel
            markers={[0, 1]}
            height={chartDimensions.height}
            width={chartDimensions.width}
            timeout={CAROUSEL_TIMEOUT}
        >
            {rotatingCharts}
        </Carousel>
    ), [rotatingCharts, chartDimensions]);

    return (
        <div className={styles.moduleWrapper}>
            <div className={classnames(styles.titleWrapper, { [styles.rightAlignTitle]: shouldRightAlignTitle, [styles.darkThemeTitleWrapper]: showDarkTheme })}>Opinions</div>
            <div className={styles.contentWrapper}>
                <div className={styles.menuOptionsWrapper}>
                    <div className={styles.shortcutMenuOptions}>
                        <HomeButtonTile path={`opinions/my-opinions/${defaultOpinionView}`} label='My Opinions' icon={Globe} testId='my-opinions' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.MY_OPINIONS))} disabledTooltip={tileDisabledTooltip(HomePageLink.MY_OPINIONS)} />
                        <HomeButtonTile path='opinions/reporting' label='Reporting' icon={Table} testId='opinions-reporting' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.OPINION_REPORTING))} disabledTooltip={tileDisabledTooltip(HomePageLink.OPINION_REPORTING)} />
                        <HomeButtonTile path='opinions/analytics' label='Analytics' icon={MultiAnalytics} testId='opinions-analytics' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.OPINION_ANALYTICS))} disabledTooltip={tileDisabledTooltip(HomePageLink.OPINION_ANALYTICS)} />
                        <HomeButtonTile path='opinions/netting-engine' label='Netting Engine' icon={Engine} testId='opinions-netting-engine' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.NETTING_ENGINE))} disabledTooltip={tileDisabledTooltip(HomePageLink.NETTING_ENGINE)} />
                    </div>
                </div>
                <div className={styles.carouselChartsWrapper}>
                    {chartDisplay}
                </div>
            </div>
        </div>
    );
};
